import type {
  CriterionKey,
  RubricScores,
  VerdictCriterion,
} from '../../utils/evaluation-rubric';
import type {
  JudgeConfig,
  JudgeExpected,
  JudgeResult,
} from '../../utils/judges';
import type { EngineRequest } from '../../server-only/engine-logs/engine-trace';
import type { ValidateTurn } from './validate-runs';
import { validateProject } from './validate-project';
import { describeSubmission } from './webview-submission';

/**
 * Calls Pro Studio's own `/ai/validate/*` routes — see
 * `src/server-only/ai/router.ts`. Same-origin plain `fetch`, like
 * `use-action-assist.ts`.
 */

export interface ScoreResponse {
  scores: RubricScores;
  autoFail: string | null;
  summary: string;
  reasons: Partial<Record<CriterionKey, string>>;
  whyFailed?: string | null;
  toneNote?: string | null;
  /** The criteria it was scored on, when the solution edited its rubric. */
  criteria?: VerdictCriterion[];
}

export interface NextTurnResponse {
  message: string | null;
  pick: string | null;
  done: boolean;
  reason: string;
}

/** A little under the server function's 60s limit, so the browser says so first. */
const TIMEOUT_MS = 55_000;

const post = async <T>(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<T> => {
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS);
  const onAbort = () => timeout.abort();
  signal?.addEventListener('abort', onAbort);
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: timeout.signal,
    });
    const json = await response.json().catch(() => null);
    if (!response.ok) {
      const b = json as { detail?: string; error?: string } | null;
      throw new Error(b?.detail ?? b?.error ?? response.statusText);
    }
    return json as T;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
};

/** Only what the prompts read — latency and ids stay in the browser. */
export const wireTranscript = (transcript: ValidateTurn[]) =>
  transcript.map(
    ({ role, text, options, seeded, system, agent, submitted }) => ({
      role,
      // What a webview handed over is part of what the customer did.
      text: submitted ? `${text}\n[${describeSubmission(submitted)}]` : text,
      ...(options?.length ? { options } : {}),
      ...(seeded ? { seeded: true } : {}),
      ...(system ? { system: true } : {}),
      ...(agent ? { agent: true } : {}),
    })
  );

/**
 * The solution's Quality score judge, as every score request carries it:
 * set by the Validate page when the solution's judges load or change, so a
 * rule added there reaches Test, scenarios and suites without each of them
 * passing it along. Null while it has no rules or examples and runs on the
 * default model — the rubric as it comes.
 */
let scoringJudge: JudgeConfig | null = null;
export const setScoringJudge = (config: JudgeConfig | null): void => {
  scoringJudge = config;
};

export const requestScore = (
  args: {
    transcript: ValidateTurn[];
    focusIndex: number | null;
    scenario?: string;
    expectation?: string;
  },
  signal?: AbortSignal
) =>
  post<ScoreResponse>(
    '/ai/validate/score',
    {
      ...args,
      transcript: wireTranscript(args.transcript),
      ...(scoringJudge ? { judge: scoringJudge } : {}),
    },
    signal
  );

/** One of the solution's judges on a whole conversation. */
export const requestJudge = (
  args: {
    judge: JudgeConfig;
    transcript: ValidateTurn[];
    scenario?: string;
    expectation?: string;
  },
  signal?: AbortSignal
) =>
  post<JudgeResult>(
    '/ai/validate/judge',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export interface ImportJudgeResponse {
  config: JudgeConfig;
  description: string;
}

/** An existing scorer prompt, turned into a judge. */
export const requestJudgeImport = (prompt: string, signal?: AbortSignal) =>
  post<ImportJudgeResponse>('/ai/validate/judge-import', { prompt }, signal);

export interface SuggestFixResponse {
  rule: string;
  why: string;
}

/** A rule that would make the judge agree with the team on a conversation. */
export const requestJudgeSuggest = (
  args: {
    judge: JudgeConfig;
    transcript: ValidateTurn[];
    team: JudgeExpected;
    result: { value: JudgeExpected; summary: string; reasons: string[] };
  },
  signal?: AbortSignal
) =>
  post<SuggestFixResponse>(
    '/ai/validate/judge-suggest',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export const requestNextTurn = (
  args: {
    scenario: string;
    expectation?: string;
    transcript: ValidateTurn[];
    turn: number;
    maxTurns: number;
  },
  signal?: AbortSignal
) =>
  post<NextTurnResponse>(
    '/ai/validate/next-turn',
    { ...args, transcript: wireTranscript(args.transcript) },
    signal
  );

export interface SeedTurnsResponse {
  messages: string[];
}

export const requestSeedTurns = (
  args: {
    scenario: string;
    expectation?: string;
    position: 'mid' | 'end';
  },
  signal?: AbortSignal
) => post<SeedTurnsResponse>('/ai/validate/seed-turns', args, signal);

/**
 * One customer message, posted by Pro Studio's server with the AI Gateway's
 * `x-ai-eg-model` override (the browser can't add that header to the
 * Engagement API). See `/ai/validate/send-message`.
 */
export const requestSendMessage = (
  args: {
    engagementApiUrl: string;
    chatId: string;
    accessToken: string;
    message: Record<string, unknown>;
    model: string;
  },
  signal?: AbortSignal
) => post<{ ok: true }>('/ai/validate/send-message', args, signal);

// Type-only: erased at build, so no server code reaches the browser.
export type {
  EngineRequest,
  EngineStep,
} from '../../server-only/engine-logs/engine-trace';

export interface EngineTraceResponse {
  requests: EngineRequest[];
  truncated: boolean;
}

/** What the bot engine walked for a chat, from the engine logs. */
/**
 * `projectId`: off localhost, logs are read only for chats in a project the
 * caller can open (`authorizeLogRead` in the router).
 */
export const requestEngineTrace = (
  args: { chatId: string; sinceMs: number },
  signal?: AbortSignal
) =>
  post<EngineTraceResponse>(
    '/ai/validate/engine-trace',
    { ...args, projectId: validateProject() },
    signal
  );

/* ---- Voice test calls (`server-only/voice/test-call.ts`) ----------------- */

/** The voice channel's Twilio account and number. Never stored or logged. */
export interface VoiceLine {
  accountSid: string;
  authToken: string;
  number: string;
  /** The channel's integrator id, from its voice webhook — how its calls are found in the logs. */
  integratorId?: string;
}

export type TestCallStatus =
  | 'queued'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'busy'
  | 'failed'
  | 'no-answer'
  | 'canceled';

export interface TestCallResponse {
  callSid: string;
  status: TestCallStatus;
  duration?: number;
}

const accountOf = (line: VoiceLine) => ({
  accountSid: line.accountSid,
  authToken: line.authToken,
});

export const requestTestCall = (line: VoiceLine, to: string) =>
  post<TestCallResponse>('/ai/voice/call-me', {
    account: accountOf(line),
    solutionNumber: line.number,
    to,
  });

export const requestTestCallStatus = (line: VoiceLine, callSid: string) =>
  post<TestCallResponse>('/ai/voice/call-status', {
    account: accountOf(line),
    callSid,
  });

export const requestHangUp = (line: VoiceLine, callSid: string) =>
  post<TestCallResponse>('/ai/voice/hang-up', {
    account: accountOf(line),
    callSid,
  });

/* ---- A "Call me" call's conversation, from the logs ---------------------- */

export interface CallRequest {
  at: number;
  userInput?: string;
  lastSaid?: string;
  stopNode?: number;
}

export interface CallEvent {
  kind:
    | 'transfer-started'
    | 'agent-answered'
    | 'no-destination'
    | 'conference-ended';
  at: number;
}

export interface CallTranscriptResponse {
  chatId: string | null;
  requests: CallRequest[];
  events: CallEvent[];
}

export const requestCallTranscript = (args: {
  /** A phone call's voice channel; not needed when `chatId` is known. */
  integratorId?: string | null;
  sinceMs: number;
  chatId?: string | null;
}) =>
  post<CallTranscriptResponse>('/ai/validate/call-transcript', {
    ...(args.integratorId ? { integratorId: args.integratorId } : {}),
    sinceMs: args.sinceMs,
    projectId: validateProject(),
    ...(args.chatId ? { chatId: args.chatId } : {}),
  });
